import type { UseQueryResult } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ApiError } from '../../lib/api-client';
import { openAuthenticatedRoute } from '../../lib/auth';
import { confirmUpdate } from '../../lib/confirm-service';
import { useToast } from '../../lib/toast';
import { useWorklistSelection } from './pathology.hooks';
import { useAdvancePathologyStatus } from './pathology.queries';
import { todayInput, type WorklistStatusOption } from './pathology.utils';
import { useTableSort } from '../../lib/table-sort';
import type { PaginatedWorklistResponse, PathologyAction, WorklistFilters } from './types';
import { WorklistFilterBar } from './WorklistFilterBar';
import { WorklistPagination } from './WorklistPagination';
import { WorklistTable } from './WorklistTable';

interface WorklistPageProps {
  eyebrow: string;
  title: string;
  description: string;
  useWorklist: (filters: WorklistFilters) => UseQueryResult<PaginatedWorklistResponse, Error>;
  /** null renders a read-only list with no checkboxes/action column (e.g. Ready Report). */
  actionLabel: string | null;
  action: PathologyAction | null;
  emptyTitle: string;
  emptyMessage: string;
  statusOptions?: WorklistStatusOption[];
  defaultStatus?: WorklistFilters['status'];
  showReportUpdate?: boolean;
  reportUpdateBulk?: boolean;
}

const initialFilters = (status?: WorklistFilters['status']): WorklistFilters => ({ page: 1, limit: 20, ...(status ? { status } : {}), fromDate: todayInput(), toDate: todayInput() });

export const WorklistPage = ({ eyebrow, title, description, useWorklist, actionLabel, action, emptyTitle, emptyMessage, statusOptions, defaultStatus, showReportUpdate = false, reportUpdateBulk = true }: WorklistPageProps) => {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<WorklistFilters>(() => initialFilters(defaultStatus));
  const [filters, setFilters] = useState<WorklistFilters>(() => initialFilters(defaultStatus));
  const [expandedBillId, setExpandedBillId] = useState<string | null>(null);
  const selection = useWorklistSelection();
  const reportSelection = useWorklistSelection();
  const { showToast } = useToast();

  const query = useWorklist(filters);
  const advance = useAdvancePathologyStatus();
  const busy = query.isFetching;
  const tableSort = useTableSort(filters.sortBy ?? null, filters.sortOrder === 'desc' ? 'desc' : 'asc', (next) => {
    setFilters((current) => ({ ...current, sortBy: next.key, sortOrder: next.direction, page: 1 }));
  });

  useEffect(() => {
    const totalPages = query.data?.pagination.totalPages;
    if (totalPages && filters.page > totalPages) setFilters((current) => ({ ...current, page: totalPages }));
  }, [filters.page, query.data?.pagination.totalPages]);

  const apply = (event: FormEvent) => { event.preventDefault(); setFilters({ ...draft, page: 1 }); };
  const reset = () => { const next = initialFilters(defaultStatus); setDraft(next); setFilters(next); };
  const today = () => { const date = todayInput(); setDraft((current) => ({ ...current, fromDate: date, toDate: date })); setFilters((current) => ({ ...current, fromDate: date, toDate: date, page: 1 })); };

  const toggleExpand = (billId: string) => { setExpandedBillId((current) => (current === billId ? null : billId)); selection.clear(); reportSelection.clear(); };

  const openReportUpload = (billId: string, lineIds: string[]) => {
    if (lineIds.length === 0) return;
    void navigate(`/pathology/report-upload/${billId}?lineIds=${lineIds.join(',')}`);
  };

  const openReportPrint = (billId: string, lineIds: string[]) => {
    if (lineIds.length === 0) return;
    openAuthenticatedRoute(`/pathology/report-print/${billId}?lineIds=${lineIds.join(',')}`);
  };

  const rollbackLines = (billId: string, lineIds: string[]) => {
    if (lineIds.length === 0) return;
    void confirmUpdate(`rollback for ${lineIds.length} selected test${lineIds.length === 1 ? '' : 's'}`).then((confirmed) => {
      if (!confirmed) return;
      advance.mutate({ billId, lineIds, action: 'rollback' }, {
        onSuccess: () => { selection.removeMany(billId, lineIds); reportSelection.removeMany(billId, lineIds); showToast('Report rolled back successfully.', 'success'); },
        onError: (error) => showToast(error instanceof ApiError ? error.message : 'The rollback could not be saved.', 'error'),
      });
    });
  };

  const advanceLines = (billId: string, lineIds: string[]) => {
    if (!action || lineIds.length === 0) return;
    void confirmUpdate(`the status of ${lineIds.length} selected test${lineIds.length === 1 ? '' : 's'}`).then((confirmed) => {
      if (!confirmed) return;
      advance.mutate({ billId, lineIds, action }, {
        onSuccess: () => { selection.removeMany(billId, lineIds); reportSelection.removeMany(billId, lineIds); showToast('Status updated successfully.', 'success'); },
        onError: (error) => showToast(error instanceof ApiError ? error.message : 'The status update could not be saved.', 'error'),
      });
    });
  };

  return (
    <div className="page-stack pathology-page">
      <div className="page-heading"><div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p>{description}</p></div></div>
      <section className="content-card investigation-bill-card pathology-card">
        <WorklistFilterBar draft={draft} onDraftChange={setDraft} onSubmit={apply} onToday={today} onReset={reset} busy={busy} statusOptions={statusOptions} />

        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState title={`Unable to load ${title.toLowerCase()}`} message={query.error instanceof ApiError ? query.error.message : 'Please try again.'} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.isSuccess && query.data.data.length === 0 ? <EmptyState title={emptyTitle} message={emptyMessage} /> : null}

        {query.isSuccess && query.data.data.length > 0 ? <>
          <WorklistTable
            bills={query.data.data}
            tableSort={tableSort}
            expandedBillId={expandedBillId}
            onToggleExpand={toggleExpand}
            isSelected={selection.isSelected}
            onToggleLine={selection.toggleLine}
            onToggleAllInBill={selection.toggleAllInBill}
            selectedInBill={selection.selectedInBill}
            actionLabel={actionLabel}
            action={action}
            actionPending={advance.isPending}
            onAdvance={advanceLines}
            onRollback={rollbackLines}
            showReportUpdate={showReportUpdate}
            reportSelection={{
              isSelected: reportSelection.isSelected,
              onToggleLine: reportSelection.toggleLine,
              onToggleAllInBill: reportSelection.toggleAllInBill,
              selectedInBill: reportSelection.selectedInBill,
            }}
            reportUpdateBulk={reportUpdateBulk}
            onOpenReportUpload={openReportUpload}
            readyReportMode={action === 'deliver-report'}
            onOpenReportPrint={openReportPrint}
          />
          <WorklistPagination
            page={filters.page}
            limit={filters.limit}
            totalPages={query.data.pagination.totalPages}
            busy={busy}
            onLimitChange={(limit) => { setDraft((current) => ({ ...current, limit })); setFilters((current) => ({ ...current, limit, page: 1 })); }}
            onPrev={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}
            onNext={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}
          />
        </> : null}
      </section>
    </div>
  );
};
